export const PWA_INSTALL_REQUEST_EVENT = "ilovemacumba:pwa-install-request";

const PWA_INSTALL_SYNC_CHANNEL = "ilovemacumba:pwa-install-sync";
const PWA_INSTALL_SYNC_STORAGE_EVENT_KEY =
  "ilovemacumba:pwa-install-sync-event";
const IN_MEMORY_REQUEST_REPLAY_WINDOW_MS = 5000;

type PwaInstallSyncEvent = {
  requestId: string;
  senderId: string;
  type: "install_request";
};

type PwaInstallInMemoryListener = (payload: PwaInstallSyncEvent) => void;

const inMemoryListeners = new Set<PwaInstallInMemoryListener>();
let lastInMemoryPayload: (PwaInstallSyncEvent & { emittedAt: number }) | null =
  null;
let isNativeInstallPromptAvailable = false;

export const setPwaInstallPromptAvailability = (isAvailable: boolean) => {
  isNativeInstallPromptAvailable = isAvailable;
};

export const isPwaInstallPromptAvailable = () => isNativeInstallPromptAvailable;

const getTabId = () => `tab-${Math.random().toString(36).slice(2, 10)}`;

const createInstallRequestEvent = (senderId: string): PwaInstallSyncEvent => ({
  requestId: `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
  senderId,
  type: "install_request",
});

export const requestPwaInstallPrompt = () => {
  if (typeof window === "undefined") {
    return;
  }

  const senderId = getTabId();
  const payload = createInstallRequestEvent(senderId);
  const emittedAt = Date.now();
  lastInMemoryPayload = { ...payload, emittedAt };

  inMemoryListeners.forEach((listener) => {
    listener(payload);
  });

  window.dispatchEvent(
    new CustomEvent<PwaInstallSyncEvent>(PWA_INSTALL_REQUEST_EVENT, {
      detail: payload,
    }),
  );

  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel(PWA_INSTALL_SYNC_CHANNEL);
    channel.postMessage(payload);
    channel.close();
  }

  try {
    localStorage.setItem(
      PWA_INSTALL_SYNC_STORAGE_EVENT_KEY,
      JSON.stringify({ ...payload, emittedAt }),
    );
    localStorage.removeItem(PWA_INSTALL_SYNC_STORAGE_EVENT_KEY);
  } catch {
    // Ignore storage event failures.
  }
};

export const subscribeToPwaInstallRequests = (
  onRequest: () => void,
): (() => void) => {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const subscriberId = getTabId();
  const handledRequestIds = new Set<string>();

  const handlePayload = (
    payload: Partial<PwaInstallSyncEvent> | null | undefined,
    options: { allowSameSender: boolean },
  ) => {
    if (!payload || payload.type !== "install_request") {
      return;
    }

    if (!options.allowSameSender && payload.senderId === subscriberId) {
      return;
    }

    if (!payload.requestId) {
      onRequest();
      return;
    }

    if (handledRequestIds.has(payload.requestId)) {
      return;
    }

    handledRequestIds.add(payload.requestId);
    onRequest();
  };

  const windowHandler = (event: Event) => {
    const customEvent = event as CustomEvent<PwaInstallSyncEvent>;

    if (customEvent.detail) {
      handlePayload(customEvent.detail, { allowSameSender: true });
      return;
    }

    onRequest();
  };

  const storageHandler = (event: StorageEvent) => {
    if (event.key !== PWA_INSTALL_SYNC_STORAGE_EVENT_KEY || !event.newValue) {
      return;
    }

    try {
      const parsed = JSON.parse(event.newValue) as Partial<PwaInstallSyncEvent>;
      handlePayload(parsed, { allowSameSender: false });
    } catch {
      // Ignore malformed events.
    }
  };

  window.addEventListener(PWA_INSTALL_REQUEST_EVENT, windowHandler);
  window.addEventListener("storage", storageHandler);

  let channel: BroadcastChannel | null = null;
  if (typeof BroadcastChannel !== "undefined") {
    channel = new BroadcastChannel(PWA_INSTALL_SYNC_CHANNEL);
    channel.onmessage = (event: MessageEvent<Partial<PwaInstallSyncEvent>>) => {
      handlePayload(event.data, { allowSameSender: false });
    };
  }

  const inMemoryListener: PwaInstallInMemoryListener = (payload) => {
    handlePayload(payload, { allowSameSender: true });
  };
  inMemoryListeners.add(inMemoryListener);

  const replayPayload = lastInMemoryPayload;
  if (
    replayPayload &&
    Date.now() - replayPayload.emittedAt <= IN_MEMORY_REQUEST_REPLAY_WINDOW_MS
  ) {
    handlePayload(replayPayload, { allowSameSender: true });
  }

  return () => {
    window.removeEventListener(PWA_INSTALL_REQUEST_EVENT, windowHandler);
    window.removeEventListener("storage", storageHandler);
    channel?.close();
    inMemoryListeners.delete(inMemoryListener);
  };
};
